/**
 * Filled Solid and Arc-by-points.
 *
 * Two placement tools the original has that were missing here: `PF` draws a
 * solid area by its corners, and `PY` draws an arc through three points rather
 * than by centre and sweep — which is how you fit a curve to geometry that is
 * already on the sheet.
 */

import type { ArcEntity, Entity, FillEntity } from '@sircad/core/model.ts';
import { LayerType, newId } from '@sircad/core/model.ts';
import type { Point } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';

/**
 * Place ▸ Filled Solid — a polygon of solid copper or ink.
 *
 * Click each corner; Enter or a double-click closes it. Three corners are the
 * fewest that enclose anything, so it refuses to finish before that rather than
 * leaving a degenerate fill in the file.
 */
export class FillTool implements Tool {
  readonly id = 'fill';
  readonly label = 'Filled Solid';
  readonly key = 'f';
  // Enter and Space place a corner (the shell's keyboard crosshair claims them
  // before a tool sees them), so closing the shape is Esc or a double-click —
  // the same pair that finishes a run of track.
  readonly hint =
    'Click each corner · Esc or double-click closes the shape · '
    + 'Backspace undoes a corner';
  readonly cursor = 'crosshair';

  readonly layerTypes = {
    sch: [LayerType.Symbols] as const,
    pcb: [LayerType.Copper, LayerType.Plane, LayerType.Silk] as const,
  };

  private points: Point[] = [];

  /** A shape with a corner down is in progress. */
  busy(): boolean {
    return this.points.length > 0;
  }

  onPointerDown(p: Point, ctx: ToolContext): void {
    const s = ctx.snap(p.x, p.y);

    // A corner on top of the last one closes the shape, which is how it is
    // finished from the keyboard now that Escape abandons instead.
    const last = this.points[this.points.length - 1];
    if (last && Math.abs(last.x - s.x) < 1e-9 && Math.abs(last.y - s.y) < 1e-9) {
      this.finish(ctx);
      return;
    }

    this.points.push({ x: s.x, y: s.y });
    this.preview(ctx);
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    if (this.points.length === 0) { ctx.redraw(); return; }
    const s = ctx.snap(p.x, p.y);
    this.preview(ctx, { x: s.x, y: s.y });
  }

  onDoubleClick(_p: Point, ctx: ToolContext): void {
    this.finish(ctx);
  }

  onKey(ev: KeyboardEvent, ctx: ToolContext): boolean {
    // Enter never arrives here — the shell takes it to place a corner at the
    // crosshair — so finishing is a repeated corner or a double click.
    if (ev.key === 'Backspace') {
      if (this.points.length === 0) return false;
      this.points.pop();
      this.preview(ctx);
      return true;
    }
    return false;
  }

  cancel(ctx: ToolContext): void {
    this.points = [];
    ctx.preview.length = 0;
    ctx.redraw();
  }

  /** The outline so far, drawn as the rubber band. */
  private preview(ctx: ToolContext, cursor?: Point): void {
    ctx.preview.length = 0;
    const pts = cursor ? [...this.points, cursor] : this.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      ctx.preview.push(seg(pts[i]!, pts[i + 1]!, ctx.activeLayer));
    }
    // Close the loop back to the start, so the area being enclosed is visible.
    if (pts.length > 2) ctx.preview.push(seg(pts[pts.length - 1]!, pts[0]!, ctx.activeLayer));
    ctx.redraw();
  }

  finish(ctx: ToolContext): void {
    if (this.points.length < 3) {
      ctx.setMessage('A filled solid needs at least three corners');
      return;
    }
    const xs = this.points.map((p) => p.x);
    const ys = this.points.map((p) => p.y);
    const fill: FillEntity = {
      kind: 'fill',
      id: newId(),
      layer: ctx.activeLayer,
      // Zero width is what makes it *solid*: the renderer reads a set width as
      // an outlined region and only fills when there is none. A Filled Solid is
      // the filled case by definition.
      width: 0,
      bounds: {
        x1: Math.min(...xs), y1: Math.min(...ys),
        x2: Math.max(...xs), y2: Math.max(...ys),
      },
      vertices: this.points.map((p) => ({ x: p.x, y: p.y })),
    };
    ctx.beginEdit('Place Filled Solid');
    ctx.doc().entities.push(fill);
    ctx.commit();
    this.points = [];
    ctx.preview.length = 0;
    ctx.setMessage(`Filled solid — ${fill.vertices.length} corners`);
    ctx.redraw();
  }
}

/**
 * Place ▸ Arc Points — an arc through three clicked points.
 *
 * Start, end, then a point the curve must pass through. Three points define
 * exactly one circle, so this fits a curve to geometry already drawn instead of
 * asking for a centre and sweep that have to be worked out first.
 */
export class ArcPointsTool implements Tool {
  readonly id = 'arc-points';
  readonly label = 'Arc by points';
  readonly key = 'y';
  readonly hint = 'Click the start, the end, then a point on the curve · Esc abandons it';
  readonly cursor = 'crosshair';

  readonly layerTypes = {
    sch: [LayerType.Symbols] as const,
    pcb: [LayerType.Drawing, LayerType.Silk] as const,
  };

  private a: Point | null = null;

  /** An arc with its first point down is in progress. */
  busy(): boolean {
    return this.a !== null;
  }
  private b: Point | null = null;

  onPointerDown(p: Point, ctx: ToolContext): void {
    const s = ctx.snap(p.x, p.y);
    const at = { x: s.x, y: s.y };
    if (!this.a) { this.a = at; ctx.setMessage('Arc — click the far end'); return; }
    if (!this.b) { this.b = at; ctx.setMessage('Arc — click a point on the curve'); return; }

    const arc = arcThrough(this.a, this.b, at, ctx);
    if (!arc) {
      ctx.setMessage('Those three points are in a straight line — no arc through them');
      return;
    }
    ctx.beginEdit('Place Arc');
    ctx.doc().entities.push(arc);
    ctx.commit();
    this.cancel(ctx);
    ctx.setMessage('Arc placed');
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    ctx.preview.length = 0;
    if (this.a && !this.b) {
      const s = ctx.snap(p.x, p.y);
      ctx.preview.push(seg(this.a, { x: s.x, y: s.y }, ctx.activeLayer));
    } else if (this.a && this.b) {
      const s = ctx.snap(p.x, p.y);
      const arc = arcThrough(this.a, this.b, { x: s.x, y: s.y }, ctx);
      if (arc) ctx.preview.push(arc);
      else ctx.preview.push(seg(this.a, this.b, ctx.activeLayer));
    }
    ctx.redraw();
  }

  cancel(ctx: ToolContext): void {
    this.a = null;
    this.b = null;
    ctx.preview.length = 0;
    ctx.redraw();
  }
}

// ---------------------------------------------------------------------------

function seg(a: Point, b: Point, layer: number): Entity {
  return {
    kind: 'line', id: -1, layer, width: 0.005,
    x1: a.x, y1: a.y, x2: b.x, y2: b.y,
  };
}

/**
 * The arc from `a` to `b` passing through `via`.
 *
 * The centre is where the perpendicular bisectors of a–via and via–b meet;
 * three points in a line have parallel bisectors and no such centre, which is
 * the null case. The sweep is chosen to be the one that actually contains
 * `via`, so the arc goes the way round the user pointed at rather than the
 * other way.
 */
function arcThrough(a: Point, b: Point, via: Point, ctx: ToolContext): ArcEntity | null {
  const d = 2 * (a.x * (via.y - b.y) + via.x * (b.y - a.y) + b.x * (a.y - via.y));
  if (Math.abs(d) < 1e-12) return null;

  const a2 = a.x * a.x + a.y * a.y;
  const v2 = via.x * via.x + via.y * via.y;
  const b2 = b.x * b.x + b.y * b.y;
  const cx = (a2 * (via.y - b.y) + v2 * (b.y - a.y) + b2 * (a.y - via.y)) / d;
  const cy = (a2 * (b.x - via.x) + v2 * (a.x - b.x) + b2 * (via.x - a.x)) / d;
  const radius = Math.hypot(a.x - cx, a.y - cy);
  if (!Number.isFinite(radius) || radius <= 0) return null;

  const deg = (p: Point): number =>
    (((Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI) + 360) % 360;
  const start = deg(a);
  const end = deg(b);
  const mid = deg(via);

  // Take the counter-clockwise sweep, then flip it if the point the user picked
  // is not inside it.
  let sweep = (end - start + 360) % 360;
  const toMid = (mid - start + 360) % 360;
  if (toMid > sweep) sweep -= 360;

  return {
    kind: 'arc', id: newId(), layer: ctx.activeLayer,
    width: ctx.doc().defaults.line?.width ?? 0.01,
    cx, cy, radius, angle: start, sweep,
  };
}
